/** Evaluations screen (tenant/evaluations.html, P6 group `build`). */
export const evaluations = {
  title: "Evaluations",
  subtitle: "Test sets are questions with expected answers. Run them after changing documents or settings to catch regressions.",
  agent: "Agent",
  runAll: "Run all",
  runAllQueued_one: "{{count}} run queued",
  runAllQueued_other: "{{count}} runs queued",
  newSetButton: "New test set",
  planFeature: "Evaluations",
  latest: {
    title: "Latest run",
    number: "#{{number}}",
    passed: "Passed threshold",
    failed: "Below threshold",
    running: "Running",
    meta: "{{set}} · {{date}} · {{trigger}}",
    compare: "Compare with #{{number}}",
    export: "Export",
    exported: "Report exported: {{file}}",
    passRate: "Pass rate",
    vsPrevious: "{{value}} cases vs run #{{number}}",
    pass: "{{count}} pass",
    partial: "{{count}} partial",
    fail: "{{count}} fail",
    model: "Model",
    chunk: "Chunk size",
    chunkValue: "{{value}} tokens",
    reranker: "Reranker",
    rerankerValue: "{{reranker}} · top-k {{k}}",
    judge: "Judge",
    duration: "Duration",
    marker: "Marker = pass threshold ({{threshold}}). A drop of more than 0.05 on any metric alerts the Owner and Admins by email.",
    noRuns: "No runs yet",
    noRunsBody: "Run a test set to see scores per metric and the cases that failed.",
  },
  metrics: {
    faithfulness: "Faithfulness (grounded)",
    faithfulnessBody: "Every claim in the answer is supported by the retrieved documents.",
    answer_correctness: "Answer correctness",
    answer_correctnessBody: "The answer matches the expected answer and addresses the question.",
    context_precision: "Context precision",
    context_precisionBody: "Retrieved chunks are relevant to the question.",
    context_recall: "Context recall",
    context_recallBody: "The expected source was found among the retrieved chunks.",
    citation_accuracy: "Citation accuracy",
    citation_accuracyBody: "Citations point to the page that really contains the fact.",
    refusal_correctness: "Refusal correctness",
    refusal_correctnessBody: "Says “I don't know” or hands off when the documents have no answer.",
    threshold: "Threshold {{value}}",
    noScore: "—",
  },
  sets: {
    title: "Test sets",
    summary: "{{sets}} sets · {{cases}} cases · scheduled runs use the agent's live settings",
    filter: "Filter test sets…",
    filterLabel: "Filter test sets",
    name: "Test set",
    agent: "Agent",
    schedule: "Schedule",
    lastRun: "Last run",
    score: "Score",
    status: "Status",
    run: "Run",
    runLabel: "Run {{name}}",
    allAgents: "All agents",
    cases_one: "{{count}} case",
    cases_other: "{{count}} cases",
    origins: { manual: "added manually", csv: "CSV upload", ai_generated: "AI-generated", conversations: "from real conversations" },
    schedules: { manual: "Manual", nightly: "Nightly 2:00 AM", on_knowledge_change: "After knowledge change" },
    health: { passing: "Passing", failing: "Failing", stale: "Stale · knowledge changed", never_run: "Never run" },
    never: "Never",
    progress: "Running…",
    queued: "Run queued · {{name}}",
    empty: "No test sets yet",
    emptyBody: "Create a test set from a CSV or let AI draft questions from your documents.",
    noMatch: "No test sets match your filter",
    open: "Open {{name}}",
  },
  history: {
    title: "Run history",
    subtitle: "{{set}} · compare configurations to see what actually improved answers",
    all: "All runs",
    configOnly: "Config changes only",
    run: "Run",
    when: "When",
    trigger: "Trigger",
    model: "Model",
    chunk: "Chunk size",
    reranker: "Reranker",
    score: "Score",
    pass: "Pass",
    latest: "Latest",
    compare: "Compare",
    compareLabel: "Compare run #{{a}} with #{{b}}",
    on: "On",
    off: "Off",
    empty: "No runs for this test set yet",
    triggers: { manual: "Manual", nightly: "Nightly", knowledge_change: "Knowledge change" },
  },
  compare: {
    title: "Run #{{a}} vs run #{{b}}",
    setting: "Setting",
    change: "Change",
    changed: "Changed",
    same: "Same",
    scores: "Scores",
    overall: "Overall score",
    passRate: "Pass rate",
    sameCases: "Same cases, same judge model.",
    differentCases: "Cases changed between these runs — compare with care.",
    useNewer: "Use newer config",
    nothingToApply: "No settings in this run can be applied to the agent.",
    applied: "Run #{{number}} settings applied to {{name}}",
    close: "Close",
  },
  failed: {
    title: "Failed cases in run #{{number}}",
    subtitle: "{{fail}} fail · {{partial}} partial · open a case to see what the agent retrieved and why the judge marked it",
    none: "No failed cases in this run",
    expected: "Expected answer",
    actual: "Actual answer",
    retrieved: "Retrieved sources",
    reasoning: "Judge reasoning",
    noReasoning: "The judge gave no reasoning for this case.",
    noAnswer: "No answer recorded.",
    verdicts: { fail: "Fail", partial: "Partial", pass: "Pass" },
    fixSource: "Review the source",
    fixKnowledge: "Open Knowledge Base",
    fixInstructions: "Edit instructions",
    accept: "Mark acceptable",
    accepted: "Marked as acceptable. The expected answer was updated.",
    acceptedBadge: "Accepted",
    source: "Source",
  },
  setSheet: {
    cases: "Cases",
    addCase: "Add case",
    question: "Question",
    expectedAnswer: "Expected answer",
    expectedSource: "Expected source",
    saveCase: "Save case",
    caseAdded: "Case added",
    deleteCase: "Delete case",
    caseDeleted: "Case deleted",
    noCases: "No cases yet — import a CSV or generate questions.",
    loadMore: "Load more",
    schedule: "Schedule",
    alert: "Email the Owner and Admins if any score drops by more than 0.05",
    saveSchedule: "Save schedule",
    scheduleSaved: "Schedule saved",
    import: "Import CSV",
    generate: "Generate with AI",
    deleteSet: "Delete test set",
    deleteTitle: "Delete {{name}}?",
    deleteBody: "Its cases and run history are deleted. This cannot be undone.",
    deleteConfirm: "Type the test set name to confirm",
    deleted: "Test set deleted",
    questionRequired: "Enter a question.",
  },
  csv: {
    label: "Upload CSV",
    paste: "Or paste CSV",
    columns: "question, expected_answer, expected_source",
    template: "Download CSV template",
    preview: "Check file",
    summary: "{{rows}} rows · {{columns}} columns detected",
    valid: "Valid",
    invalid: "Needs fixes",
    errors: "Problems",
    importButton: "Import {{count}} cases",
    imported_one: "{{count}} case imported",
    imported_other: "{{count}} cases imported",
    tooLarge: "The CSV is larger than 512 KB.",
    dropzone: {
      title: "Click to choose a CSV, or drop it here",
      hint: "CSV · up to 512 KB",
      browse: "Choose file",
    },
  },
  generate: {
    title: "Generate from documents with AI",
    body: "We draft questions and expected answers from your sources. Review them before the first run.",
    documents: "Documents to generate from",
    noDocuments: "No documents available — add knowledge first.",
    count: "Number of questions",
    taglish: "Include Taglish variants",
    refusals: "Add should-refuse questions",
    multiStep: "Multi-step questions",
    options: "Question options",
    submit: "Generate questions",
    generated_one: "{{count}} question generated",
    generated_other: "{{count}} questions generated",
  },
  newSet: {
    title: "New test set",
    name: "Name",
    namePlaceholder: "e.g. Pricing questions",
    nameRequired: "Enter a name.",
    agent: "Agent",
    aiSwitch: "Generate from documents with AI",
    schedule: "Schedule",
    manual: "Manual only",
    manualBody: "Run when you click Run",
    nightly: "Nightly",
    nightlyBody: "2:00 AM, {{timezone}}",
    onChange: "After every knowledge change",
    onChangeBody: "Upload, edit or re-sync",
    submit: "Create test set",
    created: "Test set created. First run queued.",
    createdNoRun: "Test set created.",
    csvRequired: "Add a CSV with at least one valid row, or switch on AI generation.",
  },
} as const;
